import React, { useState, useEffect, useCallback, useRef } from 'react';

const ACTIVE_STATUSES = ['placed', 'confirmed', 'preparing', 'ready'];
const THANKS_DURATION_MS = 10_000; // 10s fadeout after pickup

// Play a short pleasant 2-note chime (E5 → A5) using Web Audio API — no asset file needed.
// Browsers block audio before a user gesture, so we swallow any errors silently.
function playReadyChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, now);

    const playNote = (freq, startAt, duration) => {
      const osc = ctx.createOscillator();
      const noteGain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + startAt);
      noteGain.gain.setValueAtTime(0.0001, now + startAt);
      noteGain.gain.exponentialRampToValueAtTime(0.25, now + startAt + 0.02);
      noteGain.gain.exponentialRampToValueAtTime(0.0001, now + startAt + duration);
      osc.connect(noteGain).connect(gain);
      osc.start(now + startAt);
      osc.stop(now + startAt + duration + 0.05);
    };

    // Gentle doorbell-like chime: E5 (659.25Hz) then A5 (880Hz)
    playNote(659.25, 0, 0.35);
    playNote(880, 0.18, 0.5);

    // Close context after it finishes so we don't leak resources
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 1000);
  } catch {
    /* audio unavailable, ignore */
  }

  // Optional haptic on supported devices (phones)
  try {
    if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
  } catch { /* noop */ }
}

const STATUS_STYLE = {
  placed: { bg: '#FEF3C7', color: '#92400E', label: 'Placed' },
  confirmed: { bg: '#DBEAFE', color: '#1E40AF', label: 'Confirmed' },
  preparing: { bg: '#EDE9FE', color: '#6D28D9', label: 'Preparing' },
  ready: { bg: '#D1FAE5', color: '#065F46', label: 'Ready for Pickup' },
  picked_up: { bg: '#DCFCE7', color: '#065F46', label: 'Completed' },
  cancelled: { bg: '#FEE2E2', color: '#991B1B', label: 'Cancelled' },
};

const STEPS = [
  { key: 'placed', label: 'Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'ready', label: 'Ready' },
];

function CompactTracker({ status }) {
  const currentIdx = STEPS.findIndex((s) => s.key === status);
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginTop: '6px' }}>
      {STEPS.map((step, idx) => {
        const isDone = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <React.Fragment key={step.key}>
            <div
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                background: isDone ? '#22C55E' : isCurrent ? '#D4AF37' : 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFF',
                fontSize: '10px',
                fontWeight: '700',
                flexShrink: 0,
                boxShadow: isCurrent ? '0 0 0 3px rgba(212,175,55,0.25)' : 'none',
                animation: isCurrent ? 'ht-active-pulse 1.5s ease-in-out infinite' : 'none',
              }}
              data-testid={`home-tracker-${step.key}`}
            >
              {isDone ? '✓' : ''}
            </div>
            {idx < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: '2px',
                  background: idx < currentIdx ? '#22C55E' : 'rgba(255,255,255,0.15)',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function ActiveOrdersCard({ onSelectOrder }) {
  const [orders, setOrders] = useState([]);
  const [recentlyCompleted, setRecentlyCompleted] = useState({}); // { id: timeoutId }
  const prevStatusesRef = useRef({});
  const chimedReadyRef = useRef(new Set()); // Order IDs that have already played the ready chime
  const initialLoadDoneRef = useRef(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const fetchOrders = useCallback(async () => {
    const token = localStorage.getItem('hiway_token');
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/orders/my-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const fetched = data.orders || [];

      // Detect status transitions
      fetched.forEach((o) => {
        const prev = prevStatusesRef.current[o.id];

        // Transition → ready: play chime (only once per order, and skip on initial page load)
        if (
          o.status === 'ready' &&
          !chimedReadyRef.current.has(o.id) &&
          initialLoadDoneRef.current &&
          prev && prev !== 'ready'
        ) {
          chimedReadyRef.current.add(o.id);
          playReadyChime();
        }

        // Transition → picked_up: show thanks banner for 10s
        if (prev && ACTIVE_STATUSES.includes(prev) && o.status === 'picked_up') {
          setRecentlyCompleted((prev) => {
            if (prev[o.id]) return prev; // already tracked
            return { ...prev, [o.id]: Date.now() };
          });
          setTimeout(() => {
            setRecentlyCompleted((prev) => {
              const next = { ...prev };
              delete next[o.id];
              return next;
            });
          }, THANKS_DURATION_MS);
        }

        prevStatusesRef.current[o.id] = o.status;
      });

      initialLoadDoneRef.current = true;
      setOrders(fetched);
    } catch {
      /* silent */
    }
  }, [API_BASE]);

  // Initial fetch
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Poll every 15s when there are active orders (or recently-completed still showing)
  useEffect(() => {
    const hasActive = orders.some((o) => ACTIVE_STATUSES.includes(o.status));
    const hasThanks = Object.keys(recentlyCompleted).length > 0;
    if (!hasActive && !hasThanks) return;
    const id = setInterval(fetchOrders, 15000);
    return () => clearInterval(id);
  }, [orders, recentlyCompleted, fetchOrders]);

  // Build visible list: active orders + recently-completed orders
  const visibleOrders = orders.filter(
    (o) => ACTIVE_STATUSES.includes(o.status) || recentlyCompleted[o.id]
  );

  if (visibleOrders.length === 0) return null;

  return (
    <div style={{ padding: '0 16px', marginBottom: '4px' }}>
      <style>{`
        @keyframes ht-active-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.2); } }
        @keyframes ht-ready-glow-home { 0%,100% { box-shadow: 0 0 0 2px #22C55E, 0 4px 16px rgba(34,197,94,0.35); } 50% { box-shadow: 0 0 0 2px #22C55E, 0 6px 24px rgba(34,197,94,0.6); } }
        @keyframes ht-thanks-fade { 0% { opacity: 0; transform: translateY(-6px); } 10%,85% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-6px); } }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h2
            style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#D4AF37',
              fontFamily: "'Manrope', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {visibleOrders.length === 1 ? 'Active Order' : `${visibleOrders.length} Active Orders`}
          </h2>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '10px',
              color: '#22C55E',
              fontWeight: '600',
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#22C55E',
                animation: 'ht-active-pulse 1.5s ease-in-out infinite',
              }}
            />
            LIVE
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {visibleOrders.map((order) => {
          const isCompleted = recentlyCompleted[order.id] && order.status === 'picked_up';
          const isReady = order.status === 'ready';
          const s = STATUS_STYLE[order.status] || STATUS_STYLE.placed;

          if (isCompleted) {
            return (
              <div
                key={order.id}
                data-testid={`home-order-thanks-${order.id}`}
                style={{
                  background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                  borderRadius: '14px',
                  padding: '14px 16px',
                  color: '#FFF',
                  textAlign: 'center',
                  animation: 'ht-thanks-fade 10s ease-in-out forwards',
                }}
              >
                <p style={{ fontSize: '20px', marginBottom: '2px' }}>🙏</p>
                <p style={{ fontSize: '14px', fontWeight: '700', marginBottom: '2px' }}>
                  Thanks! Order picked up
                </p>
                <p style={{ fontSize: '11px', opacity: 0.9 }}>Hope you enjoyed {order.dhaba_name}</p>
              </div>
            );
          }

          return (
            <button
              key={order.id}
              onClick={() => onSelectOrder && onSelectOrder(order.id)}
              data-testid={`home-active-order-${order.id}`}
              style={{
                textAlign: 'left',
                padding: '14px 16px',
                borderRadius: '14px',
                background: isReady
                  ? 'linear-gradient(135deg, rgba(34,197,94,0.18), rgba(34,197,94,0.06))'
                  : 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(12px)',
                border: isReady
                  ? '2px solid #22C55E'
                  : '1px solid rgba(212, 175, 55, 0.3)',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                animation: isReady ? 'ht-ready-glow-home 2s ease-in-out infinite' : 'none',
                width: '100%',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: '#FFFFFF',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {order.dhaba_name}
                  </p>
                  <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>
                    {order.display_id || order.id.slice(0, 8).toUpperCase()}
                  </p>
                </div>
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: '8px',
                    background: s.bg,
                    color: s.color,
                    fontSize: '10px',
                    fontWeight: '700',
                    flexShrink: 0,
                    marginLeft: '8px',
                  }}
                >
                  {s.label}
                </span>
              </div>

              {isReady && (
                <p
                  style={{
                    fontSize: '12px',
                    color: '#86EFAC',
                    fontWeight: '600',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span>🛎️</span> Head to the dhaba now
                </p>
              )}

              {order.status !== 'ready' && (order.eta_minutes || order.max_prep_mins) && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '2px' }}>
                  {order.eta_minutes && (
                    <span style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '600' }}>
                      ETA {order.eta_minutes} min
                    </span>
                  )}
                  {order.max_prep_mins && (
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                      Prep {order.max_prep_mins} min
                    </span>
                  )}
                </div>
              )}

              <CompactTracker status={order.status} />

              <div
                style={{
                  marginTop: '8px',
                  fontSize: '10px',
                  color: 'rgba(255,255,255,0.4)',
                  textAlign: 'right',
                }}
              >
                Tap to view details →
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ActiveOrdersCard;
